import { useQueryClient, type UseQueryOptions } from 'react-query'

import { useMutationApi, useQueryApi } from 'utils/api'
import {
  handleError,
  validateStatus,
  apiPaths,
  queryKeys,
} from 'api/kinerja/penilik-pegawai/config'
import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'

import type { UseMutationOptions } from 'react-query'
import type { AxiosError } from 'axios'
import type {
  PraktikKinerjaFormName,
  PraktikKinerjaFormValuesRequestBody,
  PenilaianPraktikKinerjaFormValues,
  SubmissionResponse,
} from 'types/kinerja-penilik-pegawai'

export function useAPIGetPraktikKinerjaFormValues({
  skpId,
  formName,
  opts,
}: {
  skpId: string
  formName: PraktikKinerjaFormName
  opts?: UseQueryOptions<PenilaianPraktikKinerjaFormValues>
}) {
  return useQueryApi<PenilaianPraktikKinerjaFormValues>(
    apiPaths.praktikKinerjaForm(skpId, formName),
    {
      queryKey: [
        queryKeys.API_GET_PELAKSANAAN_FORM_PRAKTIK_KINERJA,
        skpId,
        formName,
      ],
      axiosConfig: {
        validateStatus,
      },
      queryConfig: {
        useErrorBoundary: false,
        ...opts,
      },
      errorHandlerFn: handleError,
    }
  )
}

export function useAPIPostPraktikKinerjaFormValues({
  skpId,
  formName,
  opts,
}: {
  skpId: string
  formName: PraktikKinerjaFormName
  opts?: UseMutationOptions<
    SubmissionResponse,
    AxiosError,
    PraktikKinerjaFormValuesRequestBody
  >
}) {
  const queryClient = useQueryClient()

  return useMutationApi<
    SubmissionResponse,
    AxiosError,
    PraktikKinerjaFormValuesRequestBody
  >(apiPaths.praktikKinerjaFormSubmit(skpId), {
    axiosConfig: { method: 'POST' },
    mutationConfig: {
      ...opts,
      onSettled() {
        queryClient.invalidateQueries([
          queryKeys.API_GET_PELAKSANAAN_FORM_PRAKTIK_KINERJA,
          skpId,
          formName,
        ])
      },
    },
  })
}

export function useAPIPostPraktikKinerjaRollbackState({
  skpId,
  opts,
}: {
  skpId: string
  opts?: UseMutationOptions<SubmissionResponse, AxiosError, {}>
}) {
  const queryClient = useQueryClient()
  const { period } = usePeriodContext()

  return useMutationApi<SubmissionResponse, AxiosError, {}>(
    `${apiPaths.praktikKinerjaFormSubmit(skpId)}/perubahan-status`,
    {
      axiosConfig: { method: 'POST' },
      mutationConfig: {
        ...opts,
        onSettled() {
          queryClient.invalidateQueries([
            queryKeys.API_GET_PELAKSANAAN_FORM_PRAKTIK_KINERJA,
            skpId,
          ])
          queryClient.invalidateQueries([queryKeys.API_GET_STATUS, period.id])
        },
      },
    }
  )
}

export function useAPIPostPraktikKinerjaRollbackStateApproval({
  skpId,
  opts,
}: {
  skpId: string
  opts?: UseMutationOptions<SubmissionResponse, AxiosError, {}>
}) {
  const queryClient = useQueryClient()
  const { period } = usePeriodContext()

  return useMutationApi<SubmissionResponse, AxiosError, {}>(
    `${apiPaths.praktikKinerjaFormSubmit(skpId)}/approval`,
    {
      axiosConfig: { method: 'POST' },
      mutationConfig: {
        ...opts,
        onSettled() {
          queryClient.invalidateQueries([
            queryKeys.API_GET_NOTIFICATION_LIST,
            period.id,
          ])
        },
      },
    }
  )
}
